import { EVENT_TRACKING, EVENT_TRACKING_ACTION, EVENT_TRACKING_ACTION_PROGRESS, EVENT_TRACKING_ACTION_SEEK, EVENT_TRACKING_ACTION_ADSKIP } from '../constants';
import round from '../../utils/round';

/**
 * Formats an event value to ensure it's an integer
 *
 * @param {number} value A numeric value for the event value
 *
 * @return {number}
 */
function formatValue (value) {
  if (typeof value === 'number') {
    return round(value, 0);
  }
  return 0;
}

/**
 * Get the analytics event value from a player event
 *
 * @param {Object} event A player event
 * @param {Object} computed
 * @param {Object} options
 * @param {Object} context
 *
 * @return {Number}
 */
function getEventValue (event, computed, options, context) {
  const eventAction = getEventAction(event, computed, context);

  if ([EVENT_TRACKING_ACTION_SEEK, EVENT_TRACKING_ACTION_ADSKIP].indexOf(eventAction) !== -1) {
    return formatValue(computed.get('percent', context));
  }

  return formatValue(computed.get('position', context));
}

/**
 * Get the analytics event label from a player event
 *
 * @param {Object} event A player event
 * @param {Object} computed
 * @param {Object} options
 * @param {Object} context
 *
 * @return {String}
 */
function getEventLabel (event, computed, options, context) {
  if (!options.label) {
    const eventAction = getEventAction(event, computed, context);

    if (eventAction === EVENT_TRACKING_ACTION_PROGRESS) {
      if (event.details.isLive) {
        return String(round(computed.get('position', context) / 60, 0));
      }
      return String(computed.get('percent', context));
    }

    return String(computed.get('title', context) || computed.get('source', context));
  }

  return String(computed.parse(options.label, context));
}

/**
 * Get the analytics event category from a player event
 *
 * @param {Object} event A player event
 * @param {Object} computed
 * @param {Object} options
 * @param {Object} context
 *
 * @return {String}
 */
function getEventCategory (event, computed, options, context) {
  if (!options.category) {
    return computed.get('contentType', context);
  }
  return computed.parse(options.category, context);
}

/**
 * Get the event action from a player event
 *
 * @param  {Object} event
 * @param  {Object} computed
 * @param  {Object} context
 *
 * @return {String}
 */
function getEventAction (event, computed, context) {
  if (context.event) {
    return context.event;
  }

  if (event.type === EVENT_TRACKING) {
    if (event.details.event === EVENT_TRACKING_ACTION) {
      return event.details.action;
    }
    return event.details.event;
  }

  return event.type;
}

/**
 * Creates a Google Analytics service
 *
 * @param {iono.Player} player  A player api instance
 * @param {Object}      computed
 * @param {Object}      logger
 * @param {Object}      options
 *
 * @return {Object}
 */
export default function GoogleAnalytics (player, computed, logger, options = {}, googleOptions = {}) {
  /**
   * Create the context for a Google Analytics event
   *
   * @param {Object} event A player event
   * @return {Object}
   */
  function createContext (event, context, mode) {
    const eventAction = getEventAction(event, computed, context);
    const eventCategory = getEventCategory(event, computed, options, context);
    const eventLabel = getEventLabel(event, computed, options, context);
    const eventValue = getEventValue(event, computed, options, context);
    const nonInteractive = eventAction === EVENT_TRACKING_ACTION_PROGRESS;

    if (mode === 'GTAG') {
      return Object.assign({
        event_action: eventAction,
        event_category: eventCategory,
        event_label: eventLabel,
        value: eventValue,
        non_interaction: nonInteractive
      }, context);
    }

    if (mode === 'GA') {
      return Object.assign({
        eventAction: eventAction,
        eventCategory: eventCategory,
        eventLabel: eventLabel,
        eventValue: eventValue,
        nonInteraction: nonInteractive
      }, context);
    }

    return context;
  }

  /**
   * Send an event to the Google Analytics service
   *
   * @param {Object} event A player event
   * @param {Object} tags  The service tags associated with the player event
   */
  function send (event, context) {
    if (typeof gtag !== 'undefined') {
      const serviceContext = createContext(event, context, 'GTAG');
      gtag('event', serviceContext.event_action, serviceContext);
    }

    if (typeof ga !== 'undefined') {
      const serviceContext = createContext(event, context, 'GA');
      ga('send', 'event', serviceContext);
    }
  }

  return {
    name: 'google',
    send
  };
}
